/* oxlint-disable confluence-feature-gating/no-preconditioning -- The master must short-circuit child gates in code to prevent child exposure in control. */
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

/**
 * Event dispatched when a comment containing an agent @mention is saved.
 * Listened to by useAgentMentionSidebarListener to open the CwR sidebar.
 */
export const AGENT_MENTION_COMMENT_SAVED_EVENT = 'confluence:agent-mention-comment-saved';

/** Refreshes a comment session after an inline action resolution without opening Chat. */
export const BACKGROUND_CONVERSATION_ACTIONS_RECONCILE_EVENT =
	'confluence:background-conversation-actions-reconcile';

/** Timeout for the agent ID resolution fetch (ms). */
export const AGENT_RESOLVE_TIMEOUT_MS = 5_000;

/** Canonical emoji ID for agent eyes reactions in writes, summaries, and actor lookups. */
export const AGENT_EYES_EMOJI_ID = 'atlassian-brown_eyes';

/** Client-only replies must never be treated as confirmation that Rovo has published. */
export const OPTIMISTIC_AGENT_REPLY_ID_PREFIX = 'optimistic-agent-reply-';

/**
 * Content types whose comment surfaces do **not** support the V1 agent
 * invocation / post-back flow. Agents must not appear in the mention picker
 * for these surfaces so users cannot start an invocation that cannot complete.
 *
 * Supported/unsupported surface matrix (V1):
 * ┌─────────────┬───────────────────────┐
 * │ Content type │ Agent mentions in     │
 * │              │ comment picker        │
 * ├─────────────┼───────────────────────┤
 * │ page        │ ✅ Supported          │
 * │ blogpost    │ ✅ Supported          │
 * │ whiteboard  │ Behind a gate         │
 * │ database    │ Behind a gate         │
 * └─────────────┴───────────────────────┘
 *
 * Whiteboards and databases stay in this set, and `isAgentMentionSupportedForContentType`
 * allows them while `canvas-rovo-comment-sessions` is on.
 */
export const AGENT_MENTION_UNSUPPORTED_CONTENT_TYPES: ReadonlySet<string> = new Set([
	'whiteboard',
	'database',
]);

/**
 * Returns `true` when the given content type supports agent mentions in its
 * comment picker for the current (V1) release.
 */
export function isAgentMentionSupportedForContentType(
	contentType: string | undefined | null,
): boolean {
	if (!contentType) {
		return false;
	}
	// The same gate turns on canvas comment sessions in Confluence and conversational-ai-platform.
	if (
		(contentType === 'whiteboard' || contentType === 'database') &&
		isExperimentEnabled('canvas_agentic_experiences_master_exp') &&
		fg('canvas-rovo-comment-sessions')
	) {
		return true;
	}
	return !AGENT_MENTION_UNSUPPORTED_CONTENT_TYPES.has(contentType);
}

// Reuse the page-session polling window when a comment loses live status updates.
export const AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT =
	'confluence:agent-session-subscription-failed';
